import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import LoadingSpinner from '../ui/LoadingSpinner';

export default function SystemAdminDashboard() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    totalInvestments: 0,
    totalVolume: 0,
    pendingKyc: 0,
    openTickets: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const customersRef = collection(db, 'customers');
        const investmentsRef = collection(db, 'investments');
        const ticketsRef = collection(db, 'tickets');

        // Fetch basic stats
        const customersSnapshot = await getDocs(customersRef);
        const activeCustomers = customersSnapshot.docs.filter(doc => doc.data().status === 'Active').length;
        const pendingKyc = customersSnapshot.docs.filter(doc => !doc.data().kyc).length;

        // Fetch investments
        const investmentsSnapshot = await getDocs(investmentsRef);
        const totalVolume = investmentsSnapshot.docs.reduce((sum, doc) => sum + (parseFloat(doc.data().amount) || 0), 0);

        // Fetch open tickets
        const ticketsQuery = query(ticketsRef, where('status', '==', 'Open'));
        const ticketsSnapshot = await getDocs(ticketsQuery);

        setStats({
          totalCustomers: customersSnapshot.size,
          activeCustomers,
          totalInvestments: investmentsSnapshot.size,
          totalVolume,
          pendingKyc,
          openTickets: ticketsSnapshot.size
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  const statCards = [
    {
      title: t('dashboard.stats.totalCustomers'),
      value: stats.totalCustomers,
      change: '+12%',
      icon: Icons.Users,
      color: 'indigo'
    },
    {
      title: t('dashboard.stats.activeCustomers'),
      value: stats.activeCustomers,
      change: '+5%',
      icon: Icons.UserCheck,
      color: 'green'
    },
    {
      title: t('dashboard.stats.totalVolume'),
      value: `${stats.totalVolume.toFixed(2)} USDT`,
      change: '+18%',
      icon: Icons.DollarSign,
      color: 'blue'
    },
    {
      title: t('dashboard.stats.totalInvestments'),
      value: stats.totalInvestments,
      change: '+7%',
      icon: Icons.PiggyBank,
      color: 'purple'
    },
    {
      title: t('dashboard.stats.pendingKyc'),
      value: stats.pendingKyc,
      icon: Icons.FileCheck,
      color: 'yellow'
    },
    {
      title: t('dashboard.stats.openTickets'),
      value: stats.openTickets,
      icon: Icons.TicketIcon,
      color: 'red'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" color="indigo" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">{stat.value}</p>
                {stat.change && (
                  <p className="mt-2 text-sm text-green-600 flex items-center">
                    <Icons.TrendingUp className="h-4 w-4 mr-1" />
                    {stat.change} {t('dashboard.stats.fromLastMonth')}
                  </p>
                )}
              </div>
              <div className={`bg-${stat.color}-100 p-3 rounded-lg`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.quickActions.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/admin/customers"
            className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icons.Users className="h-5 w-5 text-gray-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('dashboard.quickActions.manageCustomers')}
            </span>
          </Link>
          <Link
            to="/admin/kyc/pending"
            className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icons.FileCheck className="h-5 w-5 text-gray-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('dashboard.quickActions.reviewKyc')}
            </span>
          </Link>
          <Link
            to="/admin/investments"
            className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icons.PiggyBank className="h-5 w-5 text-gray-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('dashboard.quickActions.viewInvestments')}
            </span>
          </Link>
          <Link
            to="/admin/tickets"
            className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icons.TicketIcon className="h-5 w-5 text-gray-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('dashboard.quickActions.supportTickets')}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
} 